/**
 * Locales API Client with React Query Hooks
 *
 * Provides type-safe API client for Language Locales Management using:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 * - Proper error handling and transformations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost, apiPut, apiDelete } from 'services/api';
import logger from 'utils/logger';
import type {
  LocaleWithVoiceCount,
  LocaleWithDefaultVoice,
  VoiceInfo,
  CreateLocaleRequest,
  UpdateLocaleRequest,
  SetDefaultVoiceRequest,
} from '../types/locale.types';

// ============================================================================
// STEP 1: Define Zod Schemas
// ============================================================================

const LocaleWithVoiceCountSchema = z.object({
  localeCode: z.string(),
  languageCode: z.string(),
  name: z.string(),
  order: z.number(),
  voiceCount: z.number(),
  isDefault: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});

const LocaleSchema = z.object({
  localeCode: z.string(),
  languageCode: z.string(),
  name: z.string(),
  order: z.number(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});

// ============================================================================
// STEP 2: Define Transformation Functions
// ============================================================================

/**
 * Transform snake_case API response to camelCase frontend types
 */
function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }

  return transformed as T;
}

/**
 * Transform camelCase frontend types to snake_case API request
 */
function transformCamelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

    if (Array.isArray(value)) {
      transformed[snakeKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformCamelToSnake(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[snakeKey] = transformCamelToSnake(value as Record<string, unknown>);
    } else {
      transformed[snakeKey] = value;
    }
  }

  return transformed;
}

// ============================================================================
// STEP 3: Define Fetcher Functions
// ============================================================================

/**
 * Fetch all locales with voice counts from admin API
 */
const fetchLocales = async (): Promise<LocaleWithVoiceCount[]> => {
  try {
    const data = await apiGet<unknown[]>('/api/admin/locales');
    return z
      .array(LocaleWithVoiceCountSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching locales:', error);
    throw error;
  }
};

/**
 * Create a new locale
 */
const createLocale = async (request: CreateLocaleRequest): Promise<LocaleWithVoiceCount> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>('/api/admin/locales', payload);
    const locale = LocaleSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
    // Return with voiceCount = 0 and isDefault = false for new locales
    return { ...locale, voiceCount: 0, isDefault: false };
  } catch (error) {
    logger.error('Error creating locale:', error);
    throw error;
  }
};

/**
 * Update an existing locale
 */
const updateLocale = async (
  localeCode: string,
  request: UpdateLocaleRequest
): Promise<LocaleWithVoiceCount> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(`/api/admin/locales/${localeCode}`, payload);
    const locale = LocaleSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
    // Return with voiceCount and isDefault from cache
    return { ...locale, voiceCount: 0, isDefault: false };
  } catch (error) {
    logger.error('Error updating locale:', error);
    throw error;
  }
};

/**
 * Delete a locale
 */
const deleteLocale = async (localeCode: string): Promise<void> => {
  try {
    await apiDelete(`/api/admin/locales/${localeCode}`);
  } catch (error) {
    logger.error('Error deleting locale:', error);
    throw error;
  }
};

// ============================================================================
// STEP 4: Define React Query Hooks
// ============================================================================

/**
 * Fetch all locales with voice counts
 */
export const useLocales = () => {
  return useQuery({
    queryKey: ['admin-locales'],
    queryFn: fetchLocales,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Create a new locale
 */
export const useCreateLocale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateLocaleRequest) => createLocale(request),
    onSuccess: (newLocale) => {
      // Add new locale to cache
      queryClient.setQueryData<LocaleWithVoiceCount[]>(['admin-locales'], (oldData) => {
        if (!oldData) return [newLocale];
        return [...oldData, newLocale];
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-locales'] });
    },
  });
};

/**
 * Update an existing locale
 */
export const useUpdateLocale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ localeCode, request }: { localeCode: string; request: UpdateLocaleRequest }) =>
      updateLocale(localeCode, request),
    onSuccess: (updatedLocale) => {
      // Update cache with the updated locale
      queryClient.setQueryData<LocaleWithVoiceCount[]>(['admin-locales'], (oldData) => {
        if (!oldData) return [updatedLocale];
        return oldData.map((loc) =>
          loc.localeCode === updatedLocale.localeCode
            ? { ...updatedLocale, voiceCount: loc.voiceCount, isDefault: loc.isDefault }
            : loc
        );
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-locales'] });
    },
  });
};

/**
 * Delete a locale
 */
export const useDeleteLocale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (localeCode: string) => deleteLocale(localeCode),
    onSuccess: (_, localeCode) => {
      // Remove locale from cache
      queryClient.setQueryData<LocaleWithVoiceCount[]>(['admin-locales'], (oldData) => {
        if (!oldData) return [];
        return oldData.filter((loc) => loc.localeCode !== localeCode);
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-locales'] });
    },
  });
};

// ============================================================================
// DEFAULT VOICE CONFIGURATION
// ============================================================================

const VoiceInfoSchema = z.object({
  id: z.number(),
  name: z.string(),
  providerName: z.string(),
  gender: z.string(),
});

const LocaleWithDefaultVoiceSchema = z.object({
  localeCode: z.string(),
  languageCode: z.string(),
  name: z.string(),
  order: z.number(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
  defaultVoice: VoiceInfoSchema.nullable(),
});

/**
 * Fetch all locales with default voice configuration
 */
const fetchLocalesWithVoice = async (languageCode?: string): Promise<LocaleWithDefaultVoice[]> => {
  try {
    const params = languageCode ? { language_code: languageCode } : undefined;
    const data = await apiGet<unknown[]>('/api/admin/locales/default-voices', params);
    return z
      .array(LocaleWithDefaultVoiceSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching locales with voice:', error);
    throw error;
  }
};

/**
 * Fetch available voices for a locale
 */
const fetchAvailableVoices = async (localeCode: string): Promise<VoiceInfo[]> => {
  try {
    const data = await apiGet<unknown[]>(`/api/admin/locales/${localeCode}/available-voices`);
    return z
      .array(VoiceInfoSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching available voices:', error);
    throw error;
  }
};

/**
 * Set default voice for a locale
 */
const setDefaultVoice = async (
  localeCode: string,
  request: SetDefaultVoiceRequest
): Promise<LocaleWithDefaultVoice> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(`/api/admin/locales/${localeCode}/default-voice`, payload);
    return LocaleWithDefaultVoiceSchema.parse(
      transformSnakeToCamel(data as Record<string, unknown>)
    );
  } catch (error) {
    logger.error('Error setting default voice:', error);
    throw error;
  }
};

/**
 * Fetch all locales with their default voice configuration
 */
export const useLocalesWithVoice = (languageCode?: string) => {
  return useQuery({
    queryKey: ['admin-locales-with-voice', languageCode],
    queryFn: () => fetchLocalesWithVoice(languageCode),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetch available voices for a locale
 */
export const useAvailableVoices = (localeCode: string, enabled = true) => {
  return useQuery({
    queryKey: ['admin-locale-available-voices', localeCode],
    queryFn: () => fetchAvailableVoices(localeCode),
    enabled: enabled && !!localeCode,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Set default voice for a locale
 */
export const useSetDefaultVoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      localeCode,
      request,
    }: {
      localeCode: string;
      request: SetDefaultVoiceRequest;
    }) => setDefaultVoice(localeCode, request),
    onSuccess: (updatedLocale) => {
      // Update cache
      queryClient.setQueryData<LocaleWithDefaultVoice[]>(
        ['admin-locales-with-voice', undefined],
        (oldData) => {
          if (!oldData) return [updatedLocale];
          return oldData.map((loc) =>
            loc.localeCode === updatedLocale.localeCode ? updatedLocale : loc
          );
        }
      );

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-locales-with-voice'] });
    },
  });
};
